import { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "./CurrencyMasterPage";
import { Field } from "./BankAccountsPage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Trash2, ShieldCheck, KeyRound } from "lucide-react";

const ROLES = ["admin", "manager", "accountant", "data_entry"];
const MODULES = [
    "dashboard", "currencies", "entities", "banks", "partners",
    "opening_balances", "sales_invoices", "credit_notes",
    "receipts", "payments", "monthly_pnl", "reports", "users",
];
const REPORT_KEYS = [
    { key: "dashboard", label: "Dashboard KPIs" },
    { key: "receivables_payables", label: "Receivables / Payables" },
    { key: "customer_ledger", label: "Customer Ledger" },
    { key: "pnl", label: "P&L (single month)" },
    { key: "pnl_trend", label: "P&L Trend" },
    { key: "bank_position", label: "Bank Position" },
    { key: "bank_ledger", label: "Bank Ledger" },
    { key: "bank_reconciliation", label: "Bank Reconciliation" },
    { key: "nca", label: "Net Current Assets" },
    { key: "gl_ledger", label: "GL Account Ledger" },
    { key: "audit_log", label: "Audit Log" },
    { key: "vendor_reconciliation", label: "Vendor Reconciliation" },
];

export default function UsersPage() {
    const { user } = useAuth();
    const [rows, setRows] = useState([]);
    const [entities, setEntities] = useState([]);
    const [open, setOpen] = useState(false);
    const [permsUserId, setPermsUserId] = useState(null);
    const [form, setForm] = useState(blank());
    const [permState, setPermState] = useState({});
    const [reportPermState, setReportPermState] = useState({});
    const [allowedEntityState, setAllowedEntityState] = useState([]);
    const [resetUser, setResetUser] = useState(null);
    const [newPwd, setNewPwd] = useState("");
    const [confirmPwd, setConfirmPwd] = useState("");
    const [showPwd, setShowPwd] = useState(false);

    function blank() {
        return { email: "", password: "", name: "", role: "data_entry", active: true };
    }

    const load = async () => {
        const [uRes, eRes] = await Promise.all([api.get("/users"), api.get("/entities")]);
        setRows(uRes.data);
        setEntities(eRes.data || []);
    };
    useEffect(() => { load(); }, []);

    const create = async () => {
        if (!form.email || !form.password || !form.name) return toast.error("All fields required");
        try {
            await api.post("/users", form);
            toast.success("User created");
            setOpen(false);
            setForm(blank());
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    const toggleActive = async (u) => {
        await api.put(`/users/${u.id}`, { active: !u.active });
        await load();
    };

    const changeRole = async (u, role) => {
        await api.put(`/users/${u.id}`, { role });
        await load();
    };

    const remove = async (u) => {
        if (!window.confirm(`Delete user ${u.email}?`)) return;
        try {
            await api.delete(`/users/${u.id}`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    const openPerms = (u) => {
        setPermsUserId(u.id);
        setPermState(u.permissions || {});
        setReportPermState(u.report_permissions || {});
        setAllowedEntityState(u.allowed_entity_ids || []);
    };
    const togglePerm = (m, action) => {
        setPermState((s) => {
            const cur = new Set(s[m] || []);
            cur.has(action) ? cur.delete(action) : cur.add(action);
            return { ...s, [m]: [...cur] };
        });
    };
    const toggleReportPerm = (k, action) => {
        setReportPermState((s) => {
            const cur = new Set(s[k] || []);
            cur.has(action) ? cur.delete(action) : cur.add(action);
            return { ...s, [k]: [...cur] };
        });
    };
    const toggleEntity = (id) => {
        setAllowedEntityState((arr) => arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
    };
    const savePerms = async () => {
        await api.put(`/users/${permsUserId}`, {
            permissions: permState,
            report_permissions: reportPermState,
            allowed_entity_ids: allowedEntityState,
        });
        toast.success("Permissions updated");
        setPermsUserId(null);
        await load();
    };

    const openReset = (u) => {
        setResetUser(u);
        setNewPwd("");
        setConfirmPwd("");
        setShowPwd(false);
    };
    const submitReset = async () => {
        if (newPwd.length < 8) return toast.error("Password must be at least 8 characters");
        if (newPwd !== confirmPwd) return toast.error("Passwords do not match");
        try {
            await api.put(`/users/${resetUser.id}/admin-reset-password`, { new_password: newPwd });
            toast.success(`Password reset for ${resetUser.email}`);
            setResetUser(null);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to reset password");
        }
    };

    return (
        <div data-testid="users-page">
            <PageHeader
                title="Users & Permissions"
                eyebrow="Administration"
                description="Manage users, roles and module-level read/write permissions."
                action={
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button className="rounded-sm bg-[#002FA7]" data-testid="user-add-btn">+ New user</Button>
                        </DialogTrigger>
                        <DialogContent className="rounded-sm">
                            <DialogHeader><DialogTitle>Create user</DialogTitle></DialogHeader>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm h-9" data-testid="user-name-input"/></Field>
                                <Field label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm h-9" data-testid="user-email-input"/></Field>
                                <Field label="Password"><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-sm h-9" data-testid="user-password-input"/></Field>
                                <Field label="Role">
                                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                                        <SelectTrigger className="rounded-sm" data-testid="user-role-select"><SelectValue/></SelectTrigger>
                                        <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                                    </Select>
                                </Field>
                            </div>
                            <DialogFooter>
                                <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                                <Button onClick={create} className="rounded-sm bg-[#002FA7]" data-testid="user-submit-btn">Create</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                }
            />

            <div className="bg-white border border-slate-200 overflow-hidden">
                <table className="w-full text-[13px]">
                    <thead>
                        <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
                            <th className="px-4 py-2.5">Name</th>
                            <th className="px-4 py-2.5">Email</th>
                            <th className="px-4 py-2.5">Role</th>
                            <th className="px-4 py-2.5">Active</th>
                            <th className="px-4 py-2.5 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((u) => (
                            <tr key={u.id} className="border-b border-slate-100" data-testid={`user-row-${u.id}`}>
                                <td className="px-4 py-2">{u.name}</td>
                                <td className="px-4 py-2 font-mono">{u.email}</td>
                                <td className="px-4 py-2">
                                    <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                                        <SelectTrigger className="rounded-sm h-8 w-36" data-testid={`user-role-${u.id}`}><SelectValue/></SelectTrigger>
                                        <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                                    </Select>
                                </td>
                                <td className="px-4 py-2">
                                    <Switch checked={u.active} onCheckedChange={() => toggleActive(u)} data-testid={`user-active-${u.id}`}/>
                                </td>
                                <td className="px-4 py-2 text-right">
                                    <Button variant="outline" size="sm" className="h-7 rounded-sm mr-2" onClick={() => openPerms(u)} data-testid={`user-perms-${u.id}`}>
                                        <ShieldCheck size={13} className="mr-1"/> Permissions
                                    </Button>
                                    {u.id !== user?.id && (
                                        <Button variant="outline" size="sm" className="h-7 rounded-sm mr-2" onClick={() => openReset(u)} data-testid={`user-reset-pw-${u.id}`}>
                                            <KeyRound size={13} className="mr-1"/> Reset password
                                        </Button>
                                    )}
                                    {u.id !== user?.id && (
                                        <Button variant="ghost" size="sm" onClick={() => remove(u)} className="h-7 px-2 text-red-600" data-testid={`user-delete-${u.id}`}>
                                            <Trash2 size={13}/>
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Dialog open={!!permsUserId} onOpenChange={(o) => !o && setPermsUserId(null)}>
                <DialogContent className="rounded-sm max-w-2xl">
                    <DialogHeader><DialogTitle>Permissions</DialogTitle></DialogHeader>
                    <div className="max-h-[65vh] overflow-y-auto pr-2 space-y-6">
                        <div>
                            <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">Module access</div>
                            <table className="w-full text-[13px]">
                                <thead>
                                    <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                                        <th className="text-left py-2">Module</th>
                                        <th className="py-2 w-20 text-center">Read</th>
                                        <th className="py-2 w-20 text-center">Write</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {MODULES.map((m) => (
                                        <tr key={m} className="border-b border-slate-100">
                                            <td className="py-1.5 font-mono">{m}</td>
                                            <td className="py-1.5 text-center">
                                                <Checkbox checked={(permState[m] || []).includes("read")} onCheckedChange={() => togglePerm(m, "read")} data-testid={`perm-${m}-read`}/>
                                            </td>
                                            <td className="py-1.5 text-center">
                                                <Checkbox checked={(permState[m] || []).includes("write")} onCheckedChange={() => togglePerm(m, "write")} data-testid={`perm-${m}-write`}/>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div>
                            <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2 flex items-center justify-between">
                                <span>Report access (granular)</span>
                                {Object.keys(reportPermState || {}).length === 0 && (
                                    <span className="text-[10.5px] text-slate-400 normal-case tracking-normal">
                                        Leave empty to inherit module-level <span className="font-mono">reports</span> permission
                                    </span>
                                )}
                            </div>
                            <table className="w-full text-[13px]">
                                <thead>
                                    <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                                        <th className="text-left py-2">Report</th>
                                        <th className="py-2 w-20 text-center">View</th>
                                        <th className="py-2 w-20 text-center">Export / Email</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {REPORT_KEYS.map((r) => (
                                        <tr key={r.key} className="border-b border-slate-100">
                                            <td className="py-1.5">{r.label} <span className="text-slate-400 font-mono text-[11px] ml-1">({r.key})</span></td>
                                            <td className="py-1.5 text-center">
                                                <Checkbox checked={(reportPermState[r.key] || []).includes("read")} onCheckedChange={() => toggleReportPerm(r.key, "read")} data-testid={`rperm-${r.key}-read`}/>
                                            </td>
                                            <td className="py-1.5 text-center">
                                                <Checkbox checked={(reportPermState[r.key] || []).includes("write")} onCheckedChange={() => toggleReportPerm(r.key, "write")} data-testid={`rperm-${r.key}-write`}/>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div>
                            <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2 flex items-center justify-between">
                                <span>Entity scope for Bank Accounts</span>
                                <span className="text-[10.5px] text-slate-400 normal-case tracking-normal">
                                    Leave all unchecked to allow every entity
                                </span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-1.5">
                                {entities.map((e) => (
                                    <label key={e.id} className="flex items-center gap-2 text-[13px] text-slate-700 select-none">
                                        <Checkbox
                                            checked={allowedEntityState.includes(e.id)}
                                            onCheckedChange={() => toggleEntity(e.id)}
                                            data-testid={`ent-scope-${e.id}`}
                                        />
                                        <span className="font-mono text-[11.5px] text-slate-500">{e.code}</span>
                                        <span>{e.name}</span>
                                    </label>
                                ))}
                                {entities.length === 0 && <div className="text-[12px] text-slate-400">No entities configured</div>}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setPermsUserId(null)}>Cancel</Button>
                        <Button onClick={savePerms} className="rounded-sm bg-[#002FA7]" data-testid="perm-save-btn">Save permissions</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!resetUser} onOpenChange={(o) => !o && setResetUser(null)}>
                <DialogContent className="rounded-sm max-w-md" data-testid="reset-pw-dialog">
                    <DialogHeader><DialogTitle>Reset password</DialogTitle></DialogHeader>
                    {resetUser && (
                        <div className="space-y-3">
                            <div className="text-[12px] text-slate-500">
                                Set a new password for <span className="font-mono text-slate-800">{resetUser.email}</span>. The user will need to use this new password on their next login.
                            </div>
                            <Field label="New password">
                                <Input
                                    type={showPwd ? "text" : "password"}
                                    value={newPwd}
                                    onChange={(e) => setNewPwd(e.target.value)}
                                    placeholder="Min. 8 characters"
                                    className="rounded-sm h-9"
                                    autoComplete="new-password"
                                    data-testid="reset-pw-new-input"
                                />
                            </Field>
                            <Field label="Confirm password">
                                <Input
                                    type={showPwd ? "text" : "password"}
                                    value={confirmPwd}
                                    onChange={(e) => setConfirmPwd(e.target.value)}
                                    className="rounded-sm h-9"
                                    autoComplete="new-password"
                                    data-testid="reset-pw-confirm-input"
                                />
                            </Field>
                            <label className="flex items-center gap-2 text-[12px] text-slate-600 select-none">
                                <Checkbox checked={showPwd} onCheckedChange={(v) => setShowPwd(!!v)} data-testid="reset-pw-show"/>
                                Show passwords
                            </label>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setResetUser(null)} data-testid="reset-pw-cancel">Cancel</Button>
                        <Button onClick={submitReset} className="rounded-sm bg-[#002FA7]" data-testid="reset-pw-submit">Reset password</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
